/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "views/LayoutContainers/DashboardLayout";
import DashboardNavbar from "views/Navbars/DashboardNavbar";
import Footer from "views/Footer";
import EventImage from "./components/EventImage";
import Socials from "./components/Socials";
import Address from "./components/Address";
import { Link, useParams } from "react-router-dom";
import { useEventUpdateMutation, useGetEventsByIdQuery } from "../../../../store/api/events";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip } from "@mui/material";
import InfoCard from "./components/InfoCard";
import DescriptionCard from "./components/DescriptionCard";
import { useEffect, useState } from "react";
import EventCell from "../event-page/components/EventCell";
import MDBadge from "../../../../components/MDBadge";
import ParticipantsInCharge from "./components/ParticipantsInCharge";
import QRCard from "./components/QRCard";
import { notificationActions } from "../../../../store/notification/notification-slice";
import { useDispatch } from "react-redux";
import { useMeQuery } from "../../../../store/api/auth";
import { useCreateSearchRequestMutation } from "../../../../store/api/ai";
import MDProgress from "../../../../components/MDProgress";
import Card from "@mui/material/Card";


function EventDetail() {
  const { id } = useParams();

  const { data, isLoading, error } = useGetEventsByIdQuery(id, {pollingInterval: 10000});
  const [memberRow, setMemberRow] = useState();
  const dispatch = useDispatch();
  const [statusUpdate] = useEventUpdateMutation();
  const { data: user } = useMeQuery();
  const [createSearchRequest] = useCreateSearchRequestMutation();

  const searchRequestHandler = async (e) => {
    if(!open && data.photo_count !== data.processed_photo_count){
      setOpen(true);
      return;
    }
    if(open || data.photo_count === data.processed_photo_count){
    e.preventDefault();
    try {
      const payload = await createSearchRequest({
        "event": id
      });
      if(payload.error){
          dispatch(notificationActions.showMessage({
              message: payload.error.data.detail,
              variant: "warning"
          }));
      } else {
          dispatch(notificationActions.showMessage({
              message: "Created",
              variant: "success"
          }));
      }

    } catch (e) {
      for (const [key, value] of Object.entries(e.data)) {
        dispatch(notificationActions.showMessage({ message: key + ": " + value, variant: "error" }));
      }
    }
    dispatch(notificationActions.showLoading(false));
    }
    setOpen(false);
  };
  const activationHandler = async (e, id, status) => {
    e.preventDefault();
    dispatch(notificationActions.showLoading(true));
    const data = new FormData();
    data.append("id", id);
    data.append("status", status === 0 ? 4 : 0);
    try {
      const payload = await statusUpdate(data).unwrap();
      dispatch(notificationActions.showMessage({
        message: status === 0 ? "Activated" : "Deactivated",
        variant: "success"
      }));
    } catch (e) {
      for (const [key, value] of Object.entries(e.data)) {
        dispatch(notificationActions.showMessage({ message: key + ": " + value, variant: "error" }));
      }
    }
    dispatch(notificationActions.showLoading(false));

  };

  const columns = [
    { Header: "Name", accessor: "name", width: "50%" },
    { Header: "Role", accessor: "role", width: "10%" }
  ];
  useEffect(() => {
    data && setMemberRow({
      columns,
      rows: data.event_members.map(m => ({
          name: <EventCell image={m.avatar_url}
                           name={(m.first_name ? m.first_name : "") + " " + (m.last_name ? m.last_name : "")} />,
          role: <MDBadge badgeContent={m.role} color={"info"} />
        })
      )
    });
  }, [data]);

  const getString = (value) => {
    return value ? value : "";
  };
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const [open, setOpen] = useState(false);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {data &&
      <MDBox my={3}>
        <MDBox mb={6}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} lg={6}>
              <MDTypography variant="h4" fontWeight="medium">
                {data.name}
              </MDTypography>
            </Grid>
            <Grid item xs={12} lg={6}>
              {user && user.subscription.length > 0 ? (<MDBox display="flex" justifyContent="flex-end">
                  <MDBox mr={1}>
                    <Tooltip title={data.status === 0 ? "Make Active" : "Make Draft"}>
                      <MDButton variant="gradient" color={data.status === 0 ? "success" : "error"}
                                onClick={(e) => activationHandler(e, data.id, data.status)}>
                        {data.status === 0 ? "Active" : "Deactive"}
                      </MDButton>
                    </Tooltip>
                  </MDBox>
                  <Link to={"gallery"}>
                    <MDButton variant="gradient" color="info">
                      Gallery
                    </MDButton>
                  </Link>
                </MDBox>) :
                (
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDBox mr={1}>
                      <Tooltip title="To create search request, click...">
                        <MDButton variant="gradient" color={"success"}
                                  onClick={searchRequestHandler}>
                          Search My Photos
                        </MDButton>
                      </Tooltip>
                    </MDBox>
                  </MDBox>
                )
              }
            </Grid>
          </Grid>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={4}>
            <EventImage cover={data.cover} status={data.status} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <InfoCard participants={data.participant_count + ""} start_date={data.start_date}
                      end_date={data.end_date} event_code={data.event_code}
                      number_of_processed={data.processed_photo_count}
                      number_of_photos={data.photo_count}/>
          </Grid>
          <Grid item xs={12} lg={4}>
            <DescriptionCard description={getString(data.description)} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <MDBox mb={3}>
              <Socials website={getString(data.website)} twitter={getString(data.twitter)}
                       facebook={getString(data.facebook)}
                       instagram={getString(data.instagram)} />
            </MDBox>
            <MDBox mb={3}>
              <ParticipantsInCharge memberRow={memberRow} />
            </MDBox>
            <QRCard cover={data.qr_code} />
          </Grid>
          <Grid item xs={12} lg={8}>
            <Address event={data} />
          </Grid>

        </Grid>
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Remove Selected Photographs"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure? {data.photo_count - data.processed_photo_count} images are still processing and they will not include.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleClose} color="success" autoFocus>No</MDButton>
            <MDButton onClick={searchRequestHandler} color="error">
              Yes
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      }
      <Footer />
    </DashboardLayout>
  );
}

export default EventDetail;
